import React, { ChangeEvent, useRef, useState } from "react";
import { format } from "date-fns";
import { ExternalLink, FileText, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Modal } from "./ui/modal";
import { ExternalDocument, ExternalDocumentKind } from "../lib/api";
import { getBackendErrorMessage } from "../lib/utils";

interface ExternalDocumentsSectionProps {
  title: string;
  kind: ExternalDocumentKind;
  documents: ExternalDocument[];
  canUpload: boolean;
  onUpload: (file: File) => Promise<void>;
  onDelete: (documentId: string) => Promise<void>;
}

const formatFileSize = (bytes: number) => {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ExternalDocumentsSection: React.FC<ExternalDocumentsSectionProps> = ({
  title,
  kind,
  documents,
  canUpload,
  onUpload,
  onDelete,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const pendingDeleteDocument = documents.find((doc) => doc.id === pendingDeleteId);

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setIsUploading(true);
      await onUpload(file);
      toast.success(
        `${kind === "INVOICE" ? "Invoice" : "Quotation"} file uploaded`,
      );
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to upload file"));
    } finally {
      setIsUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteId) return;
    try {
      setDeletingId(pendingDeleteId);
      await onDelete(pendingDeleteId);
      toast.success("Uploaded file removed");
      setPendingDeleteId(null);
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to remove file"));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-3 pt-3 border-t border-dashed border-gray-200">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-medium text-gray-700">{title}</p>
        {canUpload && (
          <>
            <input
              ref={inputRef}
              type="file"
              className="hidden"
              accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx"
              onChange={(e) => void handleFileChange(e)}
            />
            <Button
              size="sm"
              variant="outline"
              disabled={isUploading}
              onClick={() => inputRef.current?.click()}
            >
              <Upload className="h-3.5 w-3.5 mr-1.5" />
              {isUploading ? "Uploading..." : "Upload file"}
            </Button>
          </>
        )}
      </div>

      {documents.length > 0 ? (
        <div className="space-y-2">
          {documents.map((document) => (
            <div
              key={document.id}
              className="flex items-start justify-between gap-3 p-3 rounded-lg border border-gray-100 bg-white"
            >
              <div className="flex items-start gap-3 min-w-0">
                <div className="flex-shrink-0 w-10 h-10 rounded-md border border-gray-200 bg-gray-50 flex items-center justify-center">
                  <FileText className="h-4 w-4 text-gray-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {document.originalFileName}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {formatFileSize(document.fileSize)} ·{" "}
                    {format(new Date(document.createdAt), "MMM dd, yyyy HH:mm")}
                  </p>
                  <a
                    href={document.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary-600 hover:underline inline-flex items-center gap-1 mt-1"
                  >
                    View file <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </div>
              {canUpload && (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-red-200 text-red-700 hover:bg-red-50"
                  disabled={deletingId === document.id}
                  onClick={() => setPendingDeleteId(document.id)}
                >
                  {deletingId === document.id ? "..." : "Remove"}
                </Button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500">No uploaded files yet.</p>
      )}

      <Modal
        isOpen={pendingDeleteId !== null}
        onClose={() => {
          if (!deletingId) setPendingDeleteId(null);
        }}
        title="Remove uploaded file?"
        description={
          pendingDeleteDocument
            ? `Are you sure you want to remove “${pendingDeleteDocument.originalFileName}”? This action cannot be undone.`
            : "Are you sure you want to remove this uploaded file? This action cannot be undone."
        }
        className="max-w-md"
      >
        <div className="px-6 pt-5 pb-8 flex justify-end gap-3 flex-wrap shrink-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => setPendingDeleteId(null)}
            disabled={Boolean(deletingId)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => void handleConfirmDelete()}
            disabled={Boolean(deletingId) || !pendingDeleteId}
          >
            {deletingId ? "Removing…" : "Yes, remove file"}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default ExternalDocumentsSection;
